import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { StaffProfileData } from "services/api/company/staff.service";
import type { PaymentMethodDetailsForm } from "../../profilePanel.contract";
import type {
  DiscardChangesTarget,
  ModalState,
  TranslateFn,
} from "../profilePanel.state.contract";
import {
  EMPTY_PAYMENT_METHOD_DETAILS,
  toPaymentMethodDetailsForm,
} from "../profilePanel.state.formMappers.logic";
import {
  deletePaymentMethod,
  hasPaymentMethodDetailsChanged,
  savePaymentMethod,
} from "../profilePanel.state.paymentMethod.logic";

interface UseProfilePanelPaymentMethodModalStateParams {
  companyId: number | null;
  editedPaymentMethodDetails: PaymentMethodDetailsForm;
  executeSavingAction: (action: () => Promise<void>) => void;
  isOpen: boolean;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  setDiscardChangesTarget: Dispatch<SetStateAction<DiscardChangesTarget>>;
  setEditedPaymentMethodDetails: Dispatch<
    SetStateAction<PaymentMethodDetailsForm>
  >;
  setHasTouchedPaymentMethodValue: (value: boolean) => void;
  setModalState: Dispatch<SetStateAction<ModalState>>;
  setSaveError: (value: string) => void;
  translate: TranslateFn;
  workerId: number | null;
}

interface UseProfilePanelPaymentMethodModalStateResult {
  handleOpenPaymentMethodModal: () => void;
  handleClosePaymentMethodModal: () => void;
  handleConfirmPaymentMethodModal: () => void;
  handleDeletePaymentMethodModal: () => void;
}

export const useProfilePanelPaymentMethodModalState = (
  params: UseProfilePanelPaymentMethodModalStateParams,
): UseProfilePanelPaymentMethodModalStateResult => {
  const [paymentMethodInitialDetails, setPaymentMethodInitialDetails] =
    useState<PaymentMethodDetailsForm>(EMPTY_PAYMENT_METHOD_DETAILS);

  useEffect(() => {
    if (!params.isOpen) {
      setPaymentMethodInitialDetails(EMPTY_PAYMENT_METHOD_DETAILS);
    }
  }, [params.isOpen, params.workerId]);

  const handleOpenPaymentMethodModal = useCallback((): void => {
    const nextPaymentMethodDetails = toPaymentMethodDetailsForm(params.profile);
    params.setEditedPaymentMethodDetails(nextPaymentMethodDetails);
    setPaymentMethodInitialDetails(nextPaymentMethodDetails);
    params.setHasTouchedPaymentMethodValue(false);
    params.setSaveError("");
    params.setModalState({ type: "editPaymentMethod" });
  }, [params]);

  const handleClosePaymentMethodModal = useCallback((): void => {
    if (params.isSaving) return;

    const isDirty = hasPaymentMethodDetailsChanged(
      params.editedPaymentMethodDetails,
      paymentMethodInitialDetails,
    );

    if (isDirty) {
      params.setDiscardChangesTarget("paymentMethod");
      return;
    }

    params.setSaveError("");
    params.setModalState({ type: "none" });
  }, [params, paymentMethodInitialDetails]);

  const handleConfirmPaymentMethodModal = useCallback((): void => {
    savePaymentMethod({
      companyId: params.companyId,
      editedPaymentMethodDetails: params.editedPaymentMethodDetails,
      executeSavingAction: params.executeSavingAction,
      isSaving: params.isSaving,
      onWorkerUpdated: params.onWorkerUpdated,
      profile: params.profile,
      reloadProfile: params.reloadProfile,
      setSaveError: params.setSaveError,
      closeModal: (): void => params.setModalState({ type: "none" }),
      translate: params.translate,
      workerId: params.workerId,
    });
  }, [params]);

  const handleDeletePaymentMethodModal = useCallback((): void => {
    deletePaymentMethod({
      companyId: params.companyId,
      executeSavingAction: params.executeSavingAction,
      isSaving: params.isSaving,
      onWorkerUpdated: params.onWorkerUpdated,
      profile: params.profile,
      reloadProfile: params.reloadProfile,
      setSaveError: params.setSaveError,
      closeModal: (): void => params.setModalState({ type: "none" }),
      translate: params.translate,
      workerId: params.workerId,
    });
  }, [params]);

  return {
    handleOpenPaymentMethodModal,
    handleClosePaymentMethodModal,
    handleConfirmPaymentMethodModal,
    handleDeletePaymentMethodModal,
  };
};
